import React from "react";
import { Alert, AlertTitle, Button } from "@mui/material";

import { Progress } from "ui";

import { useCustomWorkflow } from "fond/hooks/useCustomWorkflow";
import { WorkflowKey } from "fond/types";
import { useAppSelector } from "fond/utils/hooks";

const stageConfig: Record<string, { label: string }> = {
  export: { label: "Exporting base data" },
  clean: { label: "Cleaning geometries" },
  ug_network: { label: "Generating UG network" },
  // Break
  demand: { label: "Generating demands" },
  leadins: { label: "Generating lead-ins" },
  // Break
  ug_costing: { label: "Calculating UG costing" },
  output: { label: "Preparing output" },
};

interface SettingsPanelProps {
  isReadOnly?: boolean;
  canRun: boolean;
  workflowKey: WorkflowKey;
}

const SettingsPanel: React.FC<SettingsPanelProps> = ({ isReadOnly, canRun, workflowKey }) => {
  const versionId = useAppSelector((state) => state.project.versionId);
  const { workflowState, stages, runWorkflow } = useCustomWorkflow({
    versionId,
    key: workflowKey,
  });

  const isInProgress = workflowState === "STARTING" || workflowState === "IN_PROGRESS";

  const handleRunDataPrep = () => {
    runWorkflow?.();
  };

  return (
    <>
      {!canRun && workflowState === undefined && (
        <Alert severity="info" variant="compact" sx={{ mb: 1 }}>
          <AlertTitle>Upload required layers</AlertTitle>
          Please upload boundary and address layers before running data preparation.
        </Alert>
      )}

      {canRun && workflowState === undefined && (
        <Alert severity="info" variant="compact" sx={{ mb: 1 }}>
          Data is ready for preparation.
        </Alert>
      )}

      {workflowState === "ERROR" && (
        <Alert severity="error" variant="compact" sx={{ mb: 1 }}>
          <AlertTitle>Data preparation failed</AlertTitle>
          There was an error running data preparation. Please try again.
        </Alert>
      )}

      {workflowState === "COMPLETE" && (
        <Alert severity="success" variant="compact" sx={{ mb: 1 }}>
          Data preparation complete.
        </Alert>
      )}

      {stages && (isInProgress || workflowState === "ERROR") && (
        <Progress steps={stages.map(({ key, status }) => ({ ID: key, Label: stageConfig[key]?.label ?? key, Status: status }))} />
      )}

      {!isInProgress && (
        <Button
          fullWidth
          variant="contained"
          size="small"
          sx={{ mt: 2 }}
          disabled={!runWorkflow || !canRun || isInProgress || isReadOnly}
          onClick={handleRunDataPrep}
        >
          {workflowState !== "COMPLETE" ? "Start" : "Rerun"}
        </Button>
      )}
    </>
  );
};

export default SettingsPanel;
